import React, { useRef, useState } from 'react';
import { Loader } from 'lucide-react';
import Spacing from '../Spacing';
import { can_visit, getPractice, notify } from "../../helpers";
import { updatePrompt, uploadPrompt } from '../../services/prompt';
import { Drawer } from 'vaul';

export default function AddAndEditPromptModal(props) {
  const {    
    account_id,
    location_id,
    speciality_id,
    reloadData = () =>{},
    reloadDataFilter = {},
    activeModal = false,
    setActiveModal = () => {},
    editValue = {},
    editModal = false,
    previousScrollPosition = 0,
  } = props;

  const defaultValues = {
    account_id: account_id,
    location_id: location_id,
    speciality_id: editModal ? editValue?.speciality_id : speciality_id,
    ...(editModal && { id: editValue.id }),
    model: editValue?.model ? editValue?.model : "gpt-4o",
    ...(editModal && { prompt_file_url: editValue?.prompt_file_url ? editValue?.prompt_file_url : null }),
    ...(editModal ? { new_prompt_file: null } : { prompt_file: null }),
  }

  const [formData, setFormData] = useState(defaultValues);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(false);
  const dropdownRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    
    if (selectedFile && selectedFile.type !== "text/plain") {
      setFileError(true);
      setFile(null);
    } else {
      const acceptFileSize = 10 * 1024 * 1024;
      
      if (selectedFile.size > acceptFileSize) {
        notify("error", `Exceed the size limit of 10MB`, 2);
        setFileError(true);
        setFile(null);
      } else {
        setFileError(false);
        setFile(selectedFile);
        setFormData({
          ...formData,
          ...(editModal ? { new_prompt_file: selectedFile } : { prompt_file: selectedFile })
        });
      }
    }
  };

  const handleAddSubmitAction = async (e) => {
    e.preventDefault();
    setSubmitLoader(true);

    if (fileError) {
      notify("error", "Please upload a valid txt file.", 2);
      setSubmitLoader(false); 
      return;
    }

    try {
      if(can_visit("administrator") && can_visit("administrator_prompt_create")){
        const response = await uploadPrompt(formData);
      
        if (response.detail) {
          notify('error', response.detail, 2);
        } else { 
          if(response?.data?.status === "success") {
            notify("success", response.data.message, 2);
            setFormData(defaultValues);
            reloadData(reloadDataFilter);     
            handleDrawerClose();
          } else {
            notify("error", response?.data?.message, 2);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 2);
      }
    } finally {
      setSubmitLoader(false);
      setFile(null);
    }
  };

  const handleUpdateSubmitAction = async (e) => {
    e.preventDefault();
    setSubmitLoader(true);

    try {
      if(can_visit("administrator") && can_visit("administrator_prompt_edit")){
        const response = await updatePrompt(formData);
      
        if (response.detail) {
          notify('error', response.detail, 2);
        } else { 
          if(response?.data?.status === "success") {
            notify("success", response.data.message, 2);
            setFormData(defaultValues);
            reloadData(reloadDataFilter);     
            handleDrawerClose();
          } else {
            notify("error", response?.data?.message, 2);
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 2);
      }
    } finally {
      setSubmitLoader(false);
      setFile(null);
    }
  };

  const handleSubmitAction = editModal ? handleUpdateSubmitAction : handleAddSubmitAction;

  const handleDrawerClose = () => {
    setActiveModal(false);
    window.scrollTo(0, previousScrollPosition);
  };

  return (
    <div style={{ height: "100%" }} ref={dropdownRef}>
      <Drawer.Root open={activeModal} onOpenChange={handleDrawerClose}>
        <Drawer.Trigger className="docpro_single_patiant_modal_btn">
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay className="docpro_drawer_overlay"/>
          <Drawer.Content className="docpro_drawer_content docpro_drawer_style_4 medium">
            <Drawer.Title className="sr-only">Prompt Modal</Drawer.Title>
            <Drawer.Description className="sr-only">View details of a prompt</Drawer.Description>
            <div className="docpro_modal_wrap docpro_style_auto">
              <div className="docpro_modal">
                <form onSubmit={handleSubmitAction} className="modal_height_100">
                  <div>
                    <h2 className="docpro_fs_24 docpro_mb_0">{editModal ? "Edit" : "Create an"} Prompt</h2>
                    <Spacing lg="20" sm="20" />

                    <div className='w-full'>
                      <p><b>Practice: </b>{editModal ? editValue?.practice_name : getPractice("account_name") }</p>
                      <p><b>Location: </b>{editModal ? editValue?.location_name : getPractice("location_name") }</p>
                      <p><b>Speciality: </b>{editModal ? editValue?.speciality_name :  getPractice("specialities")?.find(s => s.id === speciality_id)?.name || "N/A" }</p>
                      <div>
                        <label className="docpro_fw_600 docpro_primary_color">
                          Model
                        </label>
                        <input type="text" className="docpro_form_field size_lg" name='model' onChange={handleChange} value={formData?.model} />
                      </div>

                      <Spacing lg="20" sm="20" />
                      <div>
                        <label className="docpro_fw_600 docpro_primary_color">
                          Upload {editModal ? "New" : ""} File
                        </label>
                        <div className='input_file_conatiner'>
                          <label htmlFor="file-upload" className='file_button'>
                            Choose Files
                          </label>
                          <input
                            type="file"
                            id="file-upload"
                            className='file_hidden_input'
                            accept=".txt"
                            onChange={handleFileChange}
                          />
                          <span className='file_label'>{file ? `${file?.name}` : 'No file chosen'}</span>
                        </div>
                        {fileError && (
                          <p className="docpro_error_message">
                            Please upload a valid .txt file.
                          </p>
                        )}
                      </div>
                    </div>
                    <Spacing lg="35" sm="35" />
                  </div>

                  <div className='flex items-center docpro_grid_gap_10 small_buttons docpro_mt_auto' style={{ justifyContent: "end" }}>
                    <button
                      type='button'
                      className="docpro_bordered_btn docpro_fw_600 size_lg"
                      onClick={() => handleDrawerClose()}
                    >
                      Cancel
                    </button>
                    <button
                      type='submit'
                      className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg"
                    >
                      {editModal ?
                        <span>Update</span>
                      :
                        <span>Save</span>
                      }
                      {submitLoader && (
                        <span className="docpro_loading">
                          <Loader width={20} height={20} />
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}
